import { ClientInfoStatus } from '../interfaces/client'

/**
 * The stage of a client's lifecycle in the funding pipeline. A real-world business
 * keeps one CRM identity (Company / Organization) while its financial record moves
 * Prospect → Due Diligence → Client. This maps a `client_info.client_status` to the
 * stage that decides which internal profile page it currently lives on.
 *
 * This is the shared source of truth — GlobalSearch section routing and the
 * newsfeed prospect check derive from it.
 */
export type ClientLifecycleStage = 'prospect' | 'dueDiligence' | 'client'

export function getClientLifecycleStage(
  clientStatus: string | null | undefined,
): ClientLifecycleStage | null {
  switch (clientStatus) {
    case ClientInfoStatus.Prospect:
    case ClientInfoStatus.Archived: // 'Prospect / Archived'
      return 'prospect'
    case ClientInfoStatus.DueDiligence:
      return 'dueDiligence'
    case ClientInfoStatus.Active:
    case ClientInfoStatus.Current:
    case ClientInfoStatus.Past:
    case ClientInfoStatus.PastArchived:
      return 'client'
    default:
      return null
  }
}

/**
 * Which side of the relationship a counterparty is on **for outreach purposes** —
 * who you are talking to on a call, rather than which profile page their record
 * lives on.
 *
 * This deliberately differs from {@link getClientLifecycleStage} on three
 * statuses, and the difference is the point:
 *
 *  - `Due Diligence`, `Past`, `Past / Archived` → 'prospect' here, but
 *    'dueDiligence' / 'client' there.
 *
 * A past or archived client you have a call booked with is a BD re-engagement:
 * they want the prospect-style research brief, not the current-client workflow,
 * which would try to pull live facility and availability data they no longer
 * have. Same for a company still in diligence — no facility exists yet.
 * (Prod corpus: 1,200 of 2,816 resolved Fireflies calls sit on 'Prospect /
 * Archived'; they were live prospects at call time.)
 *
 * Kept beside the lifecycle mapping on purpose — these are two intentional views
 * of one enum, and a reader who finds only one of them will assume it is the
 * only answer.
 */
export type CounterpartySide = 'client' | 'prospect'

export function getCounterpartySide(
  clientStatus: string | null | undefined,
): CounterpartySide | null {
  switch (clientStatus) {
    case ClientInfoStatus.Current:
    case ClientInfoStatus.Active:
      return 'client'
    case ClientInfoStatus.Prospect:
    case ClientInfoStatus.DueDiligence:
    case ClientInfoStatus.Archived:
    case ClientInfoStatus.Past:
    case ClientInfoStatus.PastArchived:
      return 'prospect'
    default:
      return null
  }
}
